import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import type { FreightLinkingOptionsResponse } from './types'

export const useFreightAssessorials = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const useFreightAssessorialsOverviewQuery = () =>
    useQuery({
      queryKey: ['freightAssessorialsOverviewQuery'],
      queryFn: (): OverviewResponse => api.post('freight/assessorial/overview'),
    })

  const useFreightAssessorialMetadataQuery = () =>
    useQuery({
      queryKey: ['freightAssessorialMetadataQuery'],
      queryFn: (): MetadataResponse => api.post('freight/assessorial/metadata'),
    })

  const useCreateFreightAssessorialsMutation = useMutation({
    mutationKey: ['createFreightAssessorialsMutation'],
    mutationFn: (request: CreateRequest) => api.post('freight/assessorial/create', request),
    onSuccess: (data, variables, context) => {
      queryClient.setQueriesData({ queryKey: ['freightAssessorialsOverviewQuery'] }, (prevData: OverviewResponse) => {
        return [...prevData, data]
      })
    },
  })

  const useCreateFreightAssessorialRateMutation = useMutation({
    mutationKey: ['createFreightAssessorialRateMutation'],
    mutationFn: (request: CreateRateRequest) =>
      api.post(
        'freight/assessorial/rate/create',
        {},
        {
          queryParams: new URLSearchParams({
            assessorial_id: request.assessorial_id,
            effective_date: request.effective_date,
            rate: request.rate.toString(),
          }),
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.setQueriesData({ queryKey: ['freightAssessorialsOverviewQuery'] }, (prevData: OverviewResponse) => {
        return prevData?.map((assessorial) => {
          if (assessorial._id === variables.assessorial_id) {
            return data
          }
          return assessorial
        })
      })
    },
  })

  const useDeleteAssessorialRateMutation = useMutation({
    mutationKey: ['deleteAssessorialRateMutation'],
    mutationFn: (request: DeleteRateRequest) =>
      api.post(
        'freight/assessorial/rate/delete',
        {},
        {
          queryParams: new URLSearchParams({
            assessorial_id: request.assessorial_id,
            effective_date: request.effective_date,
          }),
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.setQueriesData({ queryKey: ['freightAssessorialsOverviewQuery'] }, (prevData: OverviewResponse) => {
        return prevData?.map((assessorial) => {
          if (assessorial._id === variables.assessorial_id) {
            return data
          }
          return assessorial
        })
      })
    },
  })

  const useDeactivateFreightAssessorialsMutation = useMutation({
    mutationKey: ['deactivateFreightAssessorialsMutation'],
    mutationFn: (request: string) =>
      api.post('freight/assessorial/disable', {}, { queryParams: new URLSearchParams({ assessorial_id: request }) }),
    onSuccess: (data, variables, context) => {
      queryClient.setQueriesData({ queryKey: ['freightAssessorialsOverviewQuery'] }, (prevData: OverviewResponse) => {
        return prevData?.map((assessorial) => {
          if (assessorial._id === variables) {
            return data
          }
          return assessorial
        })
      })
    },
  })

  const specialPayOptionsResp = useQuery<FreightLinkingOptionsResponse>({
    queryKey: ['specialPayOptions'],
    queryFn: async () => api.post('payroll/special_pay/freight_linking_options'),
  })

  return {
    useFreightAssessorialsOverviewQuery,
    useFreightAssessorialMetadataQuery,
    useCreateFreightAssessorialRateMutation,
    useDeleteAssessorialRateMutation,
    useCreateFreightAssessorialsMutation,
    useDeactivateFreightAssessorialsMutation,
    specialPayOptions: specialPayOptionsResp.data || [],
  }
}
